import { useEffect, useRef, useState } from "react";
import { playlistWatchUrl, videoWatchUrl } from "../../lib/youtube";

/**
 * Embeddable YouTube player that catches Error 153 (and the related 101/150
 * "embedding not allowed for this video") via the official IFrame Player API
 * and replaces itself with a "Watch on YouTube" overlay if playback fails.
 *
 * Why bother with the API when a plain iframe works most of the time? The
 * channel owner can disable embedding either at the channel level (Studio →
 * Settings → Channel → Advanced → "Allow embedding") or per-video (Studio →
 * Edit → Visibility → "Allow embedding"). When that's off, the iframe loads
 * fine but plays back as the in-player error 153 — which is invisible to the
 * parent page and confusing for visitors. Listening to `onError` lets us
 * catch the failure and route the user out to youtube.com instead.
 *
 * Also handles the playlist case (`videoseries?list=…`) — same error codes
 * apply when the *first* video of the playlist refuses to embed.
 */

declare global {
  interface Window {
    YT?: {
      Player: new (
        el: HTMLElement | string,
        options: YtPlayerOptions,
      ) => YtPlayer;
      PlayerState?: Record<string, number>;
      ready?: (cb: () => void) => void;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YtPlayerOptions {
  videoId?: string;
  width?: string | number;
  height?: string | number;
  playerVars?: Record<string, string | number>;
  events?: {
    onReady?: (e: { target: YtPlayer }) => void;
    onStateChange?: (e: { data: number }) => void;
    onError?: (e: { data: number }) => void;
  };
}

interface YtPlayer {
  destroy: () => void;
  loadVideoById?: (id: string) => void;
  /** Optional: the IFrame API only exposes it once the player is READY. */
  playVideo?: () => void;
}

type PlayerKind =
  | { kind: "video"; id: string }
  | { kind: "playlist"; id: string };

const API_SCRIPT_SRC = "https://www.youtube.com/iframe_api";

let apiReadyPromise: Promise<void> | null = null;

function loadIframeApi(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.YT?.Player) return Promise.resolve();
  if (apiReadyPromise) return apiReadyPromise;

  apiReadyPromise = new Promise<void>((resolve) => {
    // The API will call window.onYouTubeIframeAPIReady once the script's
    // ready. Chain prior callbacks so we don't clobber another caller's
    // hook (e.g. another player mounted at the same time).
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      resolve();
    };
    // Already loaded by another component? Resolve immediately.
    if (window.YT?.Player) {
      resolve();
      return;
    }
    if (
      !document.querySelector(`script[src="${API_SCRIPT_SRC}"]`) &&
      !document.querySelector(`script[src="${API_SCRIPT_SRC.replace(/^https?:/, "")}"]`)
    ) {
      const tag = document.createElement("script");
      tag.src = API_SCRIPT_SRC;
      tag.async = true;
      document.head.appendChild(tag);
    }
  });
  return apiReadyPromise;
}

interface PlayerCommonProps {
  /** Visible title — used for aria-label and the fallback CTA. */
  title: string;
  /**
   * When true, the lite-embed (poster click → mount player) wrapper has
   * already done its job; we mount immediately. Set false when the player
   * itself acts as a poster (e.g. permanent embeds).
   */
  autoMount?: boolean;
}

type Props = PlayerCommonProps & PlayerKind;

export function YoutubePlayer(props: Props) {
  const { title, autoMount = true } = props;
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [isReady, setIsReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YtPlayer | null>(null);

  useEffect(() => {
    if (!autoMount) return;
    let cancelled = false;
    loadIframeApi().then(() => {
      if (cancelled || !containerRef.current || !window.YT?.Player) return;

      // Each Player call replaces the host element with its own iframe. We
      // give it a fresh <div> child so we can control the wrapper styles.
      const host = document.createElement("div");
      host.style.width = "100%";
      host.style.height = "100%";
      containerRef.current.innerHTML = "";
      containerRef.current.appendChild(host);

      const isPlaylist = props.kind === "playlist";
      const playerVars: Record<string, string | number> = {
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        // The player only ever mounts because someone clicked the poster, so
        // this IS their gesture — showing them YouTube's own play button and
        // asking for a second click is the embed leaking through. Autoplay
        // policies allow it for exactly this reason; `onReady` calls
        // `playVideo()` too, because the parameter alone is ignored on some
        // mobile browsers while a scripted play after READY is honoured.
        autoplay: 1,
        // enablejsapi=1 is required for onError to fire.
        enablejsapi: 1,
        // Origin helps Google's referrer checks for embeds.
        origin: window.location.origin,
      };
      if (isPlaylist) {
        playerVars.listType = "playlist";
        playerVars.list = props.id;
      }

      try {
        playerRef.current = new window.YT.Player(host, {
          width: "100%",
          height: "100%",
          videoId: isPlaylist ? undefined : props.id,
          playerVars,
          events: {
            onReady: (e: { target: YtPlayer }) => {
              if (cancelled) return;
              setIsReady(true);
              try { e.target.playVideo?.(); } catch { /* blocked — the user can press play */ }
            },
            onError: (e) => {
              // Error code map (per YT.PlayerError):
              //   2   — invalid parameter / video ID
              //   5   — HTML5 playback error
              //   100 — video removed or marked private
              //   101 — embedding restricted by owner
              //   150 — same as 101 (different reporting channel)
              // Error 153 is emitted only as the in-player message; YouTube
              // sometimes also fires onError(150) for the same case.
              if (!cancelled) setErrorCode(e.data);
            },
          },
        });
      } catch (err) {
        console.warn("[YoutubePlayer] init failed:", err);
        if (!cancelled) setErrorCode(-1);
      }
    });

    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy?.();
      } catch {
        // Player already destroyed; nothing to do.
      }
      playerRef.current = null;
    };
    // We intentionally don't depend on `props` shape changes — the player
    // is mounted once for one resource. Caller should re-key when the
    // resource id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMount, props.kind, "id" in props ? props.id : null]);

  if (errorCode !== null) {
    return <PlayerFallback {...props} code={errorCode} />;
  }

  return (
    <div
      ref={containerRef}
      className="yt-player-host"
      data-ready={isReady ? "true" : "false"}
      role="region"
      aria-label={title}
    />
  );
}

/**
 * Rendered when the iframe Player API reports a fatal error. Shows a polite
 * message and a strong CTA out to youtube.com so the user can still watch.
 */
function PlayerFallback(props: Props & { code: number }) {
  const url =
    props.kind === "playlist"
      ? playlistWatchUrl(props.id)
      : videoWatchUrl(props.id);
  const isEmbedRestricted = [101, 150, 153].includes(props.code);
  return (
    <div className="yt-player-fallback">
      <p className="yt-player-fallback-eyebrow">
        {isEmbedRestricted ? "Embedding restricted" : "Playback unavailable"}
      </p>
      <p className="yt-player-fallback-body">
        {isEmbedRestricted
          ? "This piece can be watched on YouTube directly. We can't embed it here yet."
          : `The player reported an error (code ${props.code}). The video may have been removed.`}
      </p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary mt-4 inline-flex"
      >
        <span>Watch on YouTube</span>
        <span className="btn-arrow-icon" aria-hidden>
          ↗
        </span>
      </a>
    </div>
  );
}
